import { ErrorRounded, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { useLogin } from "../api/effects/mutations/login";
import HeaderGuest from "../components/Header/HeaderGuest";
import { WarningModal } from "../components/WarningModal";
import { LoginPayload, LoginResponse } from "../models/User";
import UserState from "../recoil/UserState";
import { setCookie } from "../utils/cookies";
import { z } from "zod";
import { count } from "console";

const loginSchema = z.object({
  EMAIL: z.string().min(1, { message: "Email is required" }).email({
    message: "Must be a valid email address",
  }),
  PASSWORD: z.string().min(1, { message: "Password is required" })
    .refine((val: string) => val.trim().length > 0, {
      message: "Password is required",
    })
});

const RootGrid = styled(Grid)(({ theme }) => ({
  height: "90vh",
  [theme.breakpoints.up("md")]: {
    paddingRight: "30vw",
  },
  [theme.breakpoints.up("lg")]: {
    paddingRight: "40vw",
  },
}));

const SquareGrid = styled(Grid)({
  height: "auto",
  width: "100%",
  maxWidth: "90vw",
  padding: "2%",
  // backgroundColor: "rgba(255, 255, 255, 0.3)",
  // backgroundImage: `linear-gradient(to bottom right, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0))`,
  // backdropFilter: `blur(20px)`,
  // borderLeft: `solid 1px rgba(255, 255, 255, 0.3)`,
  // borderTop: `solid 1px rgba(255, 255, 255, 0.3)`,
  // boxShadow: "10px 10px 10px rgba(30, 30, 30, 0.1)",
  // borderRadius: "20px",
});

const ClearLink = styled(Link)({
  color: "#150A33",
  textDecoration: "none",
  fontSize: ".8em",
});

const ItemGrid = styled(Grid)({
  width: "100%",
  textAlign: "center",
  padding: "1%",
});

const ItemLinkGrid = styled(Grid)({
  width: "90%",
  height: "auto",
  textAlign: "right",
  padding: "2%",
});

const ButtonFull = styled(Button)({
  width: "40%",
});

const TextFieldFull = styled(TextField)({
  width: "90%",
});

interface LocationState {
  from: string;
}

const cooldown = 15 * 60 * 1000;
const maxAttemps = 5;
const attemptKey = "loginAttempts";
const cooldownKey = "loginCooldown";



export default function LoginPage() {

  const [user, setUser] = useState<LoginPayload>({
    EMAIL: "",
    PASSWORD: "",
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});


  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const navigate = useNavigate();

  const [warningModalState, setWarningModalState] = useState(false);
  const [warningModalText, setWarningModalText] = useState("");

  const setRecoil = useSetRecoilState(UserState);

  const location = useLocation().state as LocationState;

  const [loginAttempts, setLoginAttempts] = useState<number>(() => {
    const savedAttempts = localStorage.getItem(attemptKey);
    return savedAttempts ? parseInt(savedAttempts, 10) : 0;
  })
  const [isCoolingDown, setIsCoolingDown] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(0);

  useEffect(() => {
    const storedCooldown = localStorage.getItem(cooldownKey);
    if (storedCooldown) {
      const remainingTime = parseInt(storedCooldown, 10) - Date.now();

      if (remainingTime > 0) {
        setIsCoolingDown(true);
        setCountdown(Math.floor(remainingTime / 1000));

        // const timer = setTimeout(() => {
        //   setIsCoolingDown(false);
        //   setLoginAttempts(0);
        //   localStorage.removeItem(cooldownKey);
        //   localStorage.removeItem(attemptKey);
        // }, remainingTime)
        // return () => clearTimeout(timer);

        //   const intervalId = setInterval(() => {
        //     setCountdown(prev => {
        //       if (prev <= 1) {
        //         clearInterval(intervalId);
        //         setIsCoolingDown(false);
        //         setLoginAttempts(0);
        //         localStorage.removeItem(cooldownKey);
        //         localStorage.removeItem(attemptKey);
        //         return 0;
        //       }
        //       return prev - 1;
        //     });
        //   }, 1000);

        //   return () => clearInterval(intervalId);
      }
      else {
        localStorage.removeItem(attemptKey);
        localStorage.removeItem(cooldownKey);
      }
    }
  }, []);

  useEffect(() => {
    if (!isCoolingDown) return;

    const intervalId = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    if (countdown <= 0) {
      clearInterval(intervalId);
      setIsCoolingDown(false);
      setLoginAttempts(0);
      localStorage.removeItem(cooldownKey);
      localStorage.removeItem(attemptKey);
    }
    return () => clearInterval(intervalId);
  }, [isCoolingDown, countdown]);

  const setLoginData = async (data: LoginResponse) => {
    setCookie("token", data.token, 1);
    setRecoil({
      USER_ID: data.USER_ID,
      ROLE: data.ROLE,
      EMAIL: data.EMAIL,
    });
  };

  const { mutateAsync: login } = useLogin({
    onSuccess(data) {
      localStorage.removeItem(attemptKey);
      localStorage.removeItem(cooldownKey);
      setLoginAttempts(0);

      setLoginData(data).finally(() => {
        if (location?.from) {
          console.log(location?.from);
          navigate(location?.from, { replace: true });
        } else {
          console.log("Success home");
          navigate("/", { replace: true });
        }
      });
      console.log("Success login");
    },
    onError(error: any) {
      if (!error.response) return;
      const errorMessage = error.response.data;
      const targetError =
        "Please check your email and click the account activation link to activate your account";

      if (errorMessage === targetError) {
        const newAttempts = loginAttempts + 1;
        setLoginAttempts(newAttempts);
        localStorage.setItem(attemptKey, newAttempts.toString());

        if (newAttempts > maxAttemps) {
          const expiryTime = Date.now() + cooldown;
          localStorage.setItem(cooldownKey, expiryTime.toString());
          setIsCoolingDown(true);
          setCountdown(cooldown / 1000);
          setWarningModalState(true);

        } else {
          setWarningModalText(
            `${errorMessage} (Attempt ${newAttempts} of ${maxAttemps})`
          );
          setWarningModalState(true);
        }

      } else {
        setWarningModalText(errorMessage);
        setWarningModalState(true);
      }
    }
  })

  const loginPush = () => {
    login(user);
  };
  const onFormSubmit = (e: any) => {
    e.preventDefault();
    if (isCoolingDown) {
      setWarningModalState(true);
      return;
    }
    const validationResult = loginSchema.safeParse(user);

    if (!validationResult.success) {
      const formattedErrors: { [key: string]: string } = {};
      for (const issue of validationResult.error.issues) {
        formattedErrors[issue.path[0]] = issue.message;
      }
      setErrors(formattedErrors);
      return;
    }
    // send state to server with e.g. `window.fetch`
    setErrors({});
    loginPush();
  };

  useEffect(() => {
    if (isCoolingDown) {
      setWarningModalText(
        `Too many attempts, please try again in ${countdown}s`
      );
    }
  }, [countdown, isCoolingDown]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUser(prev => ({ ...prev, [name]: value }));
    // Clear the error for the field being typed in
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reason = params.get("reason");

    if (reason === "session_expired") {
      alert("Your session has expired. Please log in again.");
    }
  }, []);


  return (
    <Box
      sx={{
        marginTop: "0",
        height: "100%",
        minHeight: "100vh",
        width: "100%",
        backgroundImage: `url(${process.env.PUBLIC_URL}/images/TowerWhiteBG.png)`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
      }}
    >
      <WarningModal
        open={warningModalState}
        handleClose={() => setWarningModalState(false)}
        text={warningModalText}
      />
      <Fade in={true}>
        <RootGrid container justifyContent="center" alignItems="center">
          <Grid
            item
            xs={12}
            container
            alignItems="center"
            justifyContent="center"
          >
            <Box
              sx={{
                // Responsive vertical padding for each breakpoint
                pt: { xs: 2, sm: 3, md: 5, lg: 5, xl: 10 },
                pb: 0,
                // Responsive width for each breakpoint
                width: {
                  xs: 280, // for small phones
                  sm: 480, // for tablets
                  md: 540, // for small laptops
                  lg: 270, // for desktops
                  xl: 540, // for large desktops
                },
                ml: { xs: 4, sm: 4 },
              }}
            >
              <ClearLink to="/">
                <Box
                  component="img"
                  src={`${process.env.PUBLIC_URL}/images/LogoIndomobilCareer.png`}
                  alt="IndomobilCareerLogo"
                  sx={{
                    width: "100%",
                    height: "auto",
                  }}
                />
              </ClearLink>
            </Box>
          </Grid>
          <SquareGrid
            item
            xs={12}
            sm={9}
            md={6}
            container
            alignItems="center"
            justifyContent="center"
            direction="column"
          >
            <form
              onSubmit={onFormSubmit}
              style={{
                height: "auto",
                width: "100%",
                maxWidth: "90vw",
                padding: "2%",
                textAlign: "center",
              }}
            >
              <ItemGrid item>
                <Typography variant="h5">Login to your account</Typography>
              </ItemGrid>

              <ItemGrid item>
                <TextFieldFull
                  variant="outlined"
                  label="Email"
                  name="EMAIL"
                  value={user.EMAIL}
                  onChange={handleInputChange}
                  error={!!errors.EMAIL}
                  helperText={errors.EMAIL}
                />
              </ItemGrid>

              <ItemGrid item>
                <TextFieldFull
                  variant="outlined"
                  label="Password"
                  name="PASSWORD"
                  type={showPassword ? "text" : "password"}
                  value={user.PASSWORD}
                  onChange={handleInputChange}
                  error={!!errors.PASSWORD}
                  helperText={errors.PASSWORD}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="start"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </ItemGrid>

              <ItemLinkGrid container justifyContent="flex-end">
                <ClearLink to="/ForgotPassword">
                  <Typography>Forgot Password ? </Typography>
                </ClearLink>
              </ItemLinkGrid>
              <ItemGrid item>
                <ButtonFull
                  variant="outlined"
                  type="submit"
                >
                  <Typography color="black">Login</Typography>
                </ButtonFull>
              </ItemGrid>
            </form>
          </SquareGrid>
          <Grid
            item
            xs={12}
            container
            alignItems="center"
            justifyContent="center"
          >
            <ItemGrid item>
              <Typography>Don't have any account?</Typography>
            </ItemGrid>
            <ItemGrid item>
              <ClearLink to="/Register">
                <Typography color="primary">Register</Typography>
              </ClearLink>
            </ItemGrid>
          </Grid>
        </RootGrid>
      </Fade>
    </Box >
  );
}
